import { getSpeedCurveSettings, onSpeedCurveSettingsChange } from './speedCurveSettings'
import { buildSpeedColoredSegments } from './speedTrajectoryColor'
import {
  getTrajectoryColorSettings,
  isSpeedTrajectoryColorMode
} from './trajectoryColorSettings'
import { isFullPlaybackClip, slicePointsByRatioRange } from './playbackRangeClip'

export const SPEED_NAV_PATH_FLAG = '__speedNavPath'
export const SPEED_DISPLAY_PATH_FLAG = '__speedDisplayPath'

const PATH_LINE_HIDE = {
  lineWidth: 0,
  strokeStyle: 'rgba(0,0,0,0)',
  borderWidth: 0,
  borderStyle: 'rgba(0,0,0,0)',
  dirArrowStyle: false
}

const KEY_POINT_HIDE = {
  radius: 0,
  fillStyle: null,
  strokeStyle: null,
  lineWidth: 0,
  borderWidth: 0,
  borderStyle: null
}

/** 导航轨仅用于巡航/线点击，不绘制点与线 */
const NAV_PATH_STYLE = {
  pathLineStyle: { ...PATH_LINE_HIDE },
  pathLineSelectedStyle: { ...PATH_LINE_HIDE },
  pathLineHoverStyle: { ...PATH_LINE_HIDE },
  keyPointStyle: { ...KEY_POINT_HIDE },
  keyPointHoverStyle: { ...KEY_POINT_HIDE },
  keyPointOnSelectedPathLineStyle: { ...KEY_POINT_HIDE },
  startPointStyle: { ...KEY_POINT_HIDE },
  endPointStyle: { ...KEY_POINT_HIDE }
}

/** 与纯色轨迹 keyPointHoverStyle 一致（按段速度色填充） */
function buildSpeedDisplayKeyPointStyles(color) {
  const c = color || '#3366cc'
  return {
    keyPointStyle: {
      radius: 2.5,
      fillStyle: c,
      lineWidth: 1,
      strokeStyle: c
    },
    keyPointHoverStyle: {
      radius: 3.5,
      fillStyle: c,
      lineWidth: 2,
      strokeStyle: '#dc3912'
    },
    keyPointOnSelectedPathLineStyle: {
      radius: 2.5,
      fillStyle: c,
      lineWidth: 1,
      strokeStyle: c
    },
    startPointStyle: { ...KEY_POINT_HIDE },
    endPointStyle: { ...KEY_POINT_HIDE }
  }
}

function filterValidPoints(points) {
  if (!Array.isArray(points)) return []
  return points.filter(
    (p) => p && Number.isFinite(Number(p.lon)) && Number.isFinite(Number(p.lat))
  )
}

/** 与纯色模式一致：导航轨 path 与 traceList 下标一一对应，不做过滤 */
function buildTrajectoryNavPath(points) {
  if (!Array.isArray(points)) return []
  return points.map((p) => {
    if (!p) return [NaN, NaN]
    return [Number(p.lon), Number(p.lat)]
  })
}

/** 速度分段模式 PathSimplifier 容差：保证每个轨迹点都有命中区 */
export function applySpeedModePathSimplifierTolerance(opts, pointCount) {
  if (!opts) return
  opts.pathTolerance = 0
  opts.keyPointTolerance = 0
  const n = Number(pointCount)
  const safeCount = Number.isFinite(n) && n > 0 ? n : 0
  // 展示段 + 导航轨会重复计入点数，阈值需放宽以免关键点被抽稀
  opts.renderAllPointsIfNumberBelow = Math.max(100000, safeCount * 2 + 500)
}

/** @deprecated 速度分段已改由 PathSimplifier 原生点交互，保留空实现兼容旧调用 */
export function syncSpeedPointMarkerHover() {
  /* noop */
}

/** 构建 PathSimplifier 数据：展示段在下层，导航轨在最上层（巡航用） */
export function buildSpeedPathSimplifierData(points, trackName, settings, navPoints) {
  const list = Array.isArray(points) ? points : []
  const navList = Array.isArray(navPoints) ? navPoints : list
  const name = trackName || ''
  const navPath = buildTrajectoryNavPath(navList)
  if (!navPath.length) return []
  if (navPath.length < 2) {
    return [{ name, path: navPath, [SPEED_NAV_PATH_FLAG]: true }]
  }
  const segments = buildSpeedColoredSegments(list, settings)
  if (!segments.length) {
    return [{ name, path: navPath, [SPEED_NAV_PATH_FLAG]: true }]
  }
  const data = []
  segments.forEach((seg) => {
    data.push({
      name,
      path: seg.path,
      [SPEED_DISPLAY_PATH_FLAG]: true,
      speedColor: seg.color,
      pointStartIndex: seg.pointStartIndex
    })
  })
  data.push({ name, path: navPath, [SPEED_NAV_PATH_FLAG]: true })
  return data
}

/** 速度分段模式下导航轨 path 索引（位于 data 末尾） */
export function getSpeedNavPathIndex(pathSimplifierIns) {
  if (!pathSimplifierIns || typeof pathSimplifierIns.getPathData !== 'function') return 0
  try {
    let maxIdx = -1
    if (typeof pathSimplifierIns.getMaxPathIndex === 'function') {
      maxIdx = pathSimplifierIns.getMaxPathIndex()
    } else if (pathSimplifierIns._data && Array.isArray(pathSimplifierIns._data.source)) {
      maxIdx = pathSimplifierIns._data.source.length - 1
    }
    for (let i = maxIdx; i >= 0; i--) {
      const pathData = pathSimplifierIns.getPathData(i)
      if (pathData && pathData[SPEED_NAV_PATH_FLAG]) return i
    }
  } catch (e) {
    /* ignore */
  }
  return 0
}

export function isSpeedTrajectoryMultiPath(pathData) {
  return !!(pathData && (pathData[SPEED_NAV_PATH_FLAG] || pathData[SPEED_DISPLAY_PATH_FLAG]))
}

/** 解析 PathSimplifier 点/线事件中的轨迹全局点下标 */
export function resolveTracePointIndexFromEvent(pathSimplifierIns, pathIndex, pointArg) {
  let resolvedPathIndex = Number(pathIndex)
  let localIdx = -1
  if (pointArg && typeof pointArg === 'object') {
    if (Number.isFinite(Number(pointArg.pathIndex))) {
      resolvedPathIndex = Number(pointArg.pathIndex)
    }
    if (Number.isFinite(Number(pointArg.pointIndex))) {
      localIdx = Number(pointArg.pointIndex)
    } else if (Number.isFinite(Number(pointArg.idx))) {
      localIdx = Number(pointArg.idx)
    }
  } else if (Number.isFinite(Number(pointArg))) {
    localIdx = Number(pointArg)
  }
  if (localIdx < 0) return -1
  return resolveGlobalPointIndex(pathSimplifierIns, resolvedPathIndex, localIdx)
}

/** 将分段轨迹上的局部点索引映射回完整轨迹索引 */
export function resolveGlobalPointIndex(pathSimplifierIns, pathIndex, localPointIndex) {
  const localIdx = Number(localPointIndex)
  if (!Number.isFinite(localIdx) || localIdx < 0) return -1
  const pathIdx = Number(pathIndex)
  if (!Number.isFinite(pathIdx) || pathIdx < 0) return localIdx
  if (!pathSimplifierIns || typeof pathSimplifierIns.getPathData !== 'function') return localIdx
  try {
    const pathData = pathSimplifierIns.getPathData(pathIdx)
    if (pathData && pathData[SPEED_NAV_PATH_FLAG]) return localIdx
    if (pathData && Number.isFinite(Number(pathData.pointStartIndex))) {
      return Number(pathData.pointStartIndex) + localIdx
    }
  } catch (e) {
    /* ignore */
  }
  return localIdx
}

/** PathSimplifier hover 标题：展示段使用完整轨迹名与全局点序号 */
export function buildTrajectoryHoverTitle(pathData, pathIndex, pointIndex, options) {
  const {
    trackTitle,
    totalPoints,
    pathSimplifierIns
  } = options || {}
  let title = trackTitle || (pathData && pathData.name) || ''
  if (!title && pathSimplifierIns && typeof pathSimplifierIns.getPathData === 'function') {
    try {
      const navIdx = getSpeedNavPathIndex(pathSimplifierIns)
      const navPath = pathSimplifierIns.getPathData(navIdx)
      if (navPath && navPath.name) title = navPath.name
    } catch (e) {
      /* ignore */
    }
  }
  const total =
    Number(totalPoints) > 0
      ? Number(totalPoints)
      : pathData && pathData.path
        ? pathData.path.length
        : 0
  if (pointIndex >= 0) {
    const globalIdx = resolveGlobalPointIndex(pathSimplifierIns, pathIndex, pointIndex)
    const idx = globalIdx >= 0 ? globalIdx : pointIndex
    return `${title}，点：${idx}/${total}`
  }
  return `${title}，点数量${total}`
}

/** PathSimplifier getPathStyle：展示段绘制彩色线与点；导航轨仅巡航 */
export function getSpeedPathStyle(pathData, lineWidth, hidePathLineOnly) {
  if (pathData && pathData[SPEED_NAV_PATH_FLAG]) {
    return { ...NAV_PATH_STYLE }
  }
  if (pathData && pathData[SPEED_DISPLAY_PATH_FLAG] && pathData.speedColor) {
    const c = pathData.speedColor
    const w = lineWidth != null ? lineWidth : 3
    const lineStyle = hidePathLineOnly
      ? { ...PATH_LINE_HIDE }
      : {
          lineWidth: w,
          strokeStyle: c,
          borderWidth: 0,
          borderStyle: 'rgba(0,0,0,0)',
          dirArrowStyle: false
        }
    const pointStyles = buildSpeedDisplayKeyPointStyles(c)
    return {
      pathLineStyle: lineStyle,
      pathLineSelectedStyle: { ...lineStyle },
      pathLineHoverStyle: hidePathLineOnly
        ? { ...PATH_LINE_HIDE }
        : {
            lineWidth: 0,
            strokeStyle: c,
            borderWidth: 0,
            borderStyle: null,
            dirArrowStyle: false
          },
      ...pointStyles
    }
  }
  return null
}

/** 兼容旧版叠加 Polyline / 数据点圆点的清理逻辑 */
export function clearSpeedTrajectoryPolylines(host) {
  if (!host) return
  if (Array.isArray(host.polylines)) {
    host.polylines.forEach((line) => {
      try {
        if (line && host.map) host.map.remove(line)
      } catch (e) {
        /* ignore */
      }
    })
    host.polylines.length = 0
  }
  if (Array.isArray(host.pointMarkers)) {
    host.pointMarkers.forEach((marker) => {
      try {
        if (marker && typeof marker.setMap === 'function') marker.setMap(null)
      } catch (e) {
        /* ignore */
      }
    })
    host.pointMarkers.length = 0
  }
  const registry = host.pointMarkerRegistry
  if (registry && typeof registry.forEach === 'function') {
    registry.forEach((marker) => {
      try {
        if (marker && typeof marker.setMap === 'function') marker.setMap(null)
      } catch (e) {
        /* ignore */
      }
    })
    registry.clear()
  }
}

function renderPathSimplifier(pathSimplifierIns) {
  if (!pathSimplifierIns) return
  try {
    if (typeof pathSimplifierIns.renderLater === 'function') {
      pathSimplifierIns.renderLater()
    } else if (typeof pathSimplifierIns.render === 'function') {
      pathSimplifierIns.render()
    }
  } catch (e) {
    /* ignore */
  }
}

function buildSolidPathSimplifierData(points, trackName, navPoints) {
  const list = Array.isArray(points) ? points : []
  const navList = Array.isArray(navPoints) ? navPoints : list
  const displayPath = buildTrajectoryNavPath(list)
  if (!displayPath.length) return []
  const name = trackName || ''
  if (navList === list) {
    return [{ name, path: displayPath }]
  }
  const navPath = buildTrajectoryNavPath(navList)
  if (!navPath.length) return [{ name, path: displayPath }]
  return [{ name, path: displayPath }]
}

function resolveHostPlaybackClip(host) {
  if (typeof host.getPlaybackClip !== 'function') return null
  const clip = host.getPlaybackClip()
  if (!clip) return null
  const sr = Number(clip.startRatio)
  const er = Number(clip.endRatio)
  if (!Number.isFinite(sr) || !Number.isFinite(er)) return null
  if (isFullPlaybackClip(sr, er)) return null
  return {
    startRatio: Math.min(1, Math.max(0, sr)),
    endRatio: Math.min(1, Math.max(0, er))
  }
}

export function syncSpeedTrajectoryMap(host) {
  if (!host || !host.pathSimplifierIns || typeof host.getPoints !== 'function') return
  clearSpeedTrajectoryPolylines(host)
  const points = host.getPoints()
  const trackName = typeof host.getTrackName === 'function' ? host.getTrackName() : ''
  const clip = resolveHostPlaybackClip(host)
  const displayPoints = clip
    ? slicePointsByRatioRange(points, clip.startRatio, clip.endRatio).sliced
    : points
  const navPoints = clip ? displayPoints : points
  const colorSettings = getTrajectoryColorSettings()
  const useSpeedMode = !host.forceSolidTrackColor && isSpeedTrajectoryColorMode(colorSettings)
  if (useSpeedMode) {
    if (!filterValidPoints(displayPoints).length && !filterValidPoints(points).length) return
    const data = buildSpeedPathSimplifierData(
      displayPoints,
      trackName,
      getSpeedCurveSettings(),
      navPoints
    )
    if (!data.length) return
    try {
      const ins = host.pathSimplifierIns
      if (ins && typeof ins.getRenderOptions === 'function') {
        applySpeedModePathSimplifierTolerance(ins.getRenderOptions(), points.length)
      }
      ins.setData(data)
      renderPathSimplifier(ins)
    } catch (e) {
      /* ignore */
    }
    return
  }
  const data = buildSolidPathSimplifierData(displayPoints, trackName, navPoints)
  if (!data.length) return
  try {
    host.pathSimplifierIns.setData(data)
    renderPathSimplifier(host.pathSimplifierIns)
  } catch (e) {
    /* ignore */
  }
}

export function bindSpeedTrajectoryMap(getHost) {
  const refresh = () => {
    const host = typeof getHost === 'function' ? getHost() : getHost
    if (host && typeof host.refreshOverlay === 'function') {
      host.refreshOverlay()
      return
    }
    syncSpeedTrajectoryMap(host)
  }
  const unsubSpeed = onSpeedCurveSettingsChange(() => {
    if (isSpeedTrajectoryColorMode()) refresh()
  })
  return () => {
    unsubSpeed()
  }
}
